/**
 * Bundle 105 - Global drift-scan admin page.
 *
 * Route: /admin/incident/drift-scan (super_admin only)
 *
 * Renders one row per event with a survey campaign, worst-first,
 * with severity color coding. Supports single-event or bulk freeze
 * via the two-step confirmation-token flow:
 *   1. POST /freeze-all/preview   -> confirmation_token (60s TTL)
 *   2. POST /freeze-all/confirm   -> apply
 *
 * No em/en dashes, no eslint-disable, no photo/image alt.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertOctagon, RefreshCcw, Snowflake, ChevronRight,
} from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const SEV_BADGE = {
  clean: 'bg-success-50 text-success-700 border-success-200',
  outstanding_invitations_only:
    'bg-yellow-50 text-yellow-800 border-yellow-200',
  responses_affected: 'bg-red-50 text-red-800 border-red-200',
};

const SEV_LABEL = {
  clean: 'clean',
  outstanding_invitations_only: 'outstanding invitations',
  responses_affected: 'responses affected',
};

const AdminDriftScanPage = () => {
  const [aggregate, setAggregate] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [reason, setReason] = useState('bundle_104_schema_drift_incident');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  const runScan = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        '/admin/incident/drift-scan/preview',
      );
      setAggregate(data.aggregate || null);
      setEvents(data.events || []);
    } catch (e) {
      toast.error('Could not run drift scan.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runScan(); }, []);

  const selectedIds = useMemo(() => (
    Object.entries(selected)
      .filter(([, v]) => v)
      .map(([k]) => k)
  ), [selected]);

  const toggle = (eid) => setSelected((s) => ({ ...s, [eid]: !s[eid] }));

  const toggleAllActionable = () => {
    const actionable = events.filter((r) => r.severity !== 'clean');
    const allOn = actionable.every((r) => selected[r.event_id]);
    const next = { ...selected };
    actionable.forEach((r) => { next[r.event_id] = !allOn; });
    setSelected(next);
  };

  const startFreeze = async (ids) => {
    if (!ids.length) { toast.error('Select at least one event.'); return; }
    if (!reason.trim()) { toast.error('Reason is required.'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/drift-scan/freeze-all/preview',
        { event_ids: ids, reason: reason.trim() },
      );
      setConfirmDialog({ preview: data, mintedAt: Date.now() });
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Freeze preview failed.';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const applyFreeze = async () => {
    if (!confirmDialog?.preview?.confirmation_token) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/drift-scan/freeze-all/confirm',
        {
          confirmation_token:
            confirmDialog.preview.confirmation_token,
          reason: confirmDialog.preview.reason,
        },
      );
      toast.success(
        `Froze ${data.processed} events (${data.invalidated_invitations} invitations invalidated).`,
      );
      setConfirmDialog(null);
      setSelected({});
      runScan();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Confirm failed.';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  // Bundle 108 - one-shot backfill for the Bundle 107 same-id
  // reissue data-integrity gap.
  const [backfillDialog, setBackfillDialog] = useState(null);

  const startBackfill = async (eventId) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/backfill-bundle107-reissue',
        { event_id: eventId, dry_run: true },
      );
      setBackfillDialog({ event_id: eventId, preview: data });
    } catch (e) {
      toast.error(e?.response?.data?.detail
                        || 'Backfill preview failed.');
    } finally { setBusy(false); }
  };

  const applyBackfill = async () => {
    if (!backfillDialog?.event_id) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/backfill-bundle107-reissue',
        { event_id: backfillDialog.event_id, dry_run: false },
      );
      toast.success(
        `Backfill applied. Minted ${data.minted_count}, already fine ${data.already_fine_count}, skipped ${data.skipped_missing_inv_count}.`,
      );
      setBackfillDialog(null);
      runScan();
    } catch (e) {
      toast.error(e?.response?.data?.detail
                        || 'Backfill apply failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="p-8 space-y-6 max-w-7xl"
          data-testid="admin-drift-scan-page">
      <div className="flex items-center gap-2 text-xs text-neutral-500">
        <Link to="/admin/surveys" className="hover:text-primary-900">
          Survey Ops
        </Link>
        <ChevronRight className="h-3 w-3" />
        <span>Drift scan</span>
      </div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2">
            <AlertOctagon className="h-5 w-5 text-red-700" />
            <h1 className="font-display text-3xl font-bold text-primary-900">
              Global drift scan
            </h1>
          </div>
          <p className="text-neutral-600 text-sm max-w-2xl mt-2">
            Every event with a survey campaign is checked against the
            current Bundle 89+ canonical schema and each of its
            submitted responses is inspected for pre-Bundle-89 option
            values. Freezing an event pauses its campaign, invalidates
            outstanding invitations, and stamps drifted responses as
            audit-only. This is idempotent.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={runScan}
                    data-testid="drift-scan-refresh-btn"
                    disabled={loading}>
            <RefreshCcw className="h-4 w-4 mr-2" />
            {loading ? 'Scanning...' : 'Re-run scan'}
          </Button>
        </div>
      </div>

      {aggregate ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3"
              data-testid="drift-scan-aggregate">
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Events scanned</div>
            <div className="text-2xl font-display font-bold text-primary-900 mt-1"
                  data-testid="drift-scan-total-events">
              {aggregate.events_total}
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Clean</div>
            <div className="text-2xl font-display font-bold text-success-700 mt-1"
                  data-testid="drift-scan-clean-count">
              {aggregate.events_clean}
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Outstanding only</div>
            <div className="text-2xl font-display font-bold text-yellow-800 mt-1"
                  data-testid="drift-scan-outstanding-count">
              {aggregate.events_outstanding_invitations_only}
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Responses affected</div>
            <div className="text-2xl font-display font-bold text-red-800 mt-1"
                  data-testid="drift-scan-responses-affected-count">
              {aggregate.events_responses_affected}
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Drifted responses</div>
            <div className="text-2xl font-display font-bold text-red-800 mt-1"
                  data-testid="drift-scan-drifted-responses">
              {aggregate.total_drifted_responses}
            </div>
          </CardContent></Card>
        </div>
      ) : null}

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-xs uppercase tracking-widest text-neutral-500">
              Freeze reason
            </div>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
                    className="max-w-md text-sm"
                    data-testid="drift-scan-reason-input" />
            <Button variant="outline" onClick={toggleAllActionable}
                      data-testid="drift-scan-select-all-btn">
              Select all with drift
            </Button>
            <Button
              className="bg-red-700 text-white hover:bg-red-800"
              disabled={!selectedIds.length || busy}
              onClick={() => startFreeze(selectedIds)}
              data-testid="drift-scan-freeze-selected-btn"
            >
              <Snowflake className="h-4 w-4 mr-2" />
              Freeze {selectedIds.length} selected
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm"
                  data-testid="drift-scan-table">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-widest text-neutral-500">
              <tr>
                <th className="p-3 w-8"></th>
                <th className="p-3">Event</th>
                <th className="p-3">Severity</th>
                <th className="p-3">Campaign</th>
                <th className="p-3 text-right">Sent</th>
                <th className="p-3 text-right">Submitted</th>
                <th className="p-3 text-right">Drifted invs</th>
                <th className="p-3 text-right">Drifted resps</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="p-6 text-center text-neutral-500">Scanning...</td></tr>
              ) : events.length === 0 ? (
                <tr><td colSpan={9} className="p-6 text-center text-neutral-500"
                              data-testid="drift-scan-empty">
                  No campaigns to scan.
                </td></tr>
              ) : events.map((r) => {
                const actionable = r.severity !== 'clean';
                return (
                  <tr key={r.event_id}
                        className={`border-t border-neutral-100 ${actionable ? '' : 'opacity-70'}`}
                        data-testid={`drift-scan-row-${r.event_id}`}>
                    <td className="p-3">
                      <Checkbox
                        checked={!!selected[r.event_id]}
                        onCheckedChange={() => toggle(r.event_id)}
                        disabled={!actionable}
                        data-testid={`drift-scan-check-${r.event_id}`}
                      />
                    </td>
                    <td className="p-3">
                      <div className="text-primary-900 font-medium">{r.event_name || '(untitled)'}</div>
                      <div className="text-xs text-neutral-500">
                        {r.event_date
                          ? new Date(r.event_date).toLocaleDateString()
                          : ''}
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge className={`${SEV_BADGE[r.severity]} border`}
                              data-testid={`drift-scan-severity-${r.event_id}`}>
                        {SEV_LABEL[r.severity]}
                      </Badge>
                      {r.campaign_status === 'paused' ? (
                        <div className="text-xs text-neutral-500 mt-1">
                          paused
                        </div>
                      ) : null}
                    </td>
                    <td className="p-3 text-xs text-neutral-600">
                      {r.campaign_status}
                    </td>
                    <td className="p-3 text-right text-neutral-700">{r.sent_count}</td>
                    <td className="p-3 text-right text-neutral-700">{r.submitted_count}</td>
                    <td className={`p-3 text-right ${r.drifted_invitation_count ? 'text-yellow-800 font-semibold' : 'text-neutral-500'}`}>
                      {r.drifted_invitation_count}
                    </td>
                    <td className={`p-3 text-right ${r.drifted_response_count ? 'text-red-800 font-semibold' : 'text-neutral-500'}`}>
                      {r.drifted_response_count}
                    </td>
                    <td className="p-3 flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline"
                                onClick={() => startFreeze([r.event_id])}
                                disabled={!actionable || busy}
                                data-testid={`drift-scan-freeze-btn-${r.event_id}`}>
                        Freeze
                      </Button>
                      <Button size="sm" variant="outline"
                                onClick={() => startBackfill(r.event_id)}
                                disabled={busy}
                                data-testid={`drift-scan-backfill-btn-${r.event_id}`}>
                        Backfill B107
                      </Button>
                      <Link to={`/admin/events/${r.event_id}/survey/respondents`}>
                        <Button size="sm" variant="ghost"
                                  data-testid={`drift-scan-drilldown-btn-${r.event_id}`}>
                          Respondents
                        </Button>
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!confirmDialog}
                onOpenChange={(v) => { if (!v) setConfirmDialog(null); }}>
        <DialogContent className="max-w-lg"
                        data-testid="drift-scan-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm bulk freeze</DialogTitle>
          </DialogHeader>
          {confirmDialog?.preview ? (
            <div className="space-y-3 text-sm">
              <div className="text-neutral-700">
                You are about to freeze
                {' '}
                <span className="font-semibold text-red-800"
                        data-testid="drift-scan-confirm-count">
                  {confirmDialog.preview.actionable_event_ids?.length || 0}
                </span>
                {' '}events with reason
                {' '}
                <code className="bg-neutral-100 px-1 py-0.5 rounded text-xs">
                  {confirmDialog.preview.reason}
                </code>
                .
              </div>
              <ul className="max-h-48 overflow-y-auto border border-neutral-200 rounded-md divide-y text-xs">
                {(confirmDialog.preview.validation || []).map((v) => (
                  <li key={v.event_id} className="p-2"
                        data-testid={`drift-scan-confirm-row-${v.event_id}`}>
                    <div className="font-mono">{v.event_id}</div>
                    {v.ok
                      ? (v.already_paused
                          ? <span className="text-neutral-500">already paused (freeze re-runs safely)</span>
                          : <span className="text-primary-900">will freeze</span>)
                      : <span className="text-red-800">skipped: {v.reason}</span>}
                  </li>
                ))}
              </ul>
              <div className="text-xs text-neutral-500">
                Confirmation token expires in {confirmDialog.preview.expires_in_s || 60} seconds.
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDialog(null)}
                      data-testid="drift-scan-confirm-cancel-btn">
              Cancel
            </Button>
            <Button className="bg-red-700 text-white hover:bg-red-800"
                      onClick={applyFreeze}
                      disabled={busy}
                      data-testid="drift-scan-confirm-apply-btn">
              <Snowflake className="h-4 w-4 mr-2" />
              Freeze now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!backfillDialog}
                onOpenChange={(v) => { if (!v) setBackfillDialog(null); }}>
        <DialogContent className="max-w-lg"
                        data-testid="drift-scan-backfill-dialog">
          <DialogHeader>
            <DialogTitle>Bundle 107 reissue backfill</DialogTitle>
          </DialogHeader>
          {backfillDialog?.preview ? (
            <div className="space-y-3 text-sm">
              <div className="text-neutral-700">
                Dry-run for event
                {' '}
                <code className="bg-neutral-100 px-1 py-0.5 rounded text-xs">
                  {backfillDialog.event_id?.slice(-12)}
                </code>
              </div>
              <ul className="text-xs border border-neutral-200 rounded-md divide-y">
                <li className="p-2 flex justify-between">
                  <span>To mint (missing new invitation rows)</span>
                  <span className="font-semibold"
                          data-testid="backfill-to-mint-count">
                    {backfillDialog.preview.to_mint_count}
                  </span>
                </li>
                <li className="p-2 flex justify-between">
                  <span>Already fine</span>
                  <span className="font-mono">
                    {backfillDialog.preview.already_fine_count}
                  </span>
                </li>
                <li className="p-2 flex justify-between">
                  <span>Skipped (missing old row)</span>
                  <span className="font-mono">
                    {backfillDialog.preview.skipped_missing_inv_count}
                  </span>
                </li>
              </ul>
              <div className="text-xs text-neutral-500">
                Apply is idempotent. Running twice does nothing extra.
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBackfillDialog(null)}
                      data-testid="backfill-cancel-btn">
              Cancel
            </Button>
            <Button className="bg-primary-900 text-white hover:bg-primary-800"
                      onClick={applyBackfill}
                      disabled={busy || !backfillDialog?.preview?.to_mint_count}
                      data-testid="backfill-apply-btn">
              Apply backfill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDriftScanPage;
